import React, {useMemo} from 'react';
import {IconButton, styled} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  RECRUITMENT_STATUS_PENDING_VETTING,
  RECRUITMENT_STATUS_VETTING_FAILED,
  type Recruitment,
} from '@/views/creators/recruit-crm/types';
import Dropdown, {DropdownOption} from '@/components/dropdown';

interface RecruitmentActionsMenuProps {
  recruitment: Recruitment;
  onRemove: (recruitmentId: string) => void;
  onRestoreToCrm: (recruitment: Recruitment) => void;
  onStartVetting: (recruitment: Recruitment) => void;
  isStartVettingLoading: boolean;
}

const EMPTY_SELECTED_IDS: string[] = [];

const OPTION_IDS = {
  remove: 'remove',
  restoreToCrm: 'restore-to-crm',
  startVetting: 'start-vetting',
} as const;

const StyledIconButton = styled(IconButton)({
  height: '30px',
  width: '30px',
});

const StyledIcon = styled(Icon)({
  transform: 'scale(1.5)',
});

function RecruitmentActionsMenu({
  recruitment,
  onRemove,
  onRestoreToCrm,
  onStartVetting,
  isStartVettingLoading,
}: RecruitmentActionsMenuProps) {
  const options = useMemo<DropdownOption[]>(() => {
    const items: DropdownOption[] = [];

    if (
      !recruitment.removed &&
      [
        RECRUITMENT_STATUS_PENDING_VETTING,
        RECRUITMENT_STATUS_VETTING_FAILED,
      ].includes(recruitment.status)
    ) {
      items.push({
        id: OPTION_IDS.startVetting,
        label: translate('Start Vetting'),
        startIcon: (
          <Icon
            name="Navigation-Shopbag-Shield-Stroke"
            size="medium"
            appearance="neutral"
          />
        ),
        disabled: isStartVettingLoading,
      });
    }

    if (recruitment.removed) {
      items.push({
        id: OPTION_IDS.restoreToCrm,
        label: translate('Restore to CRM'),
        startIcon: (
          <Icon
            name="Navigation-Creator-Add"
            size="medium"
            appearance="neutral"
          />
        ),
      });
    } else {
      items.push({
        id: OPTION_IDS.remove,
        label: translate('Remove'),
        startIcon: (
          <Icon name="Actions-Remove" size="medium" appearance="neutral" />
        ),
      });
    }

    return items;
  }, [recruitment.removed, recruitment.status, isStartVettingLoading]);

  const handleChange = (option: DropdownOption) => {
    if (option.id === OPTION_IDS.remove) {
      onRemove(recruitment.id);
    } else if (option.id === OPTION_IDS.restoreToCrm) {
      onRestoreToCrm(recruitment);
    } else if (option.id === OPTION_IDS.startVetting) {
      onStartVetting(recruitment);
    }
  };

  return (
    <Dropdown
      label=""
      options={options}
      selectedIds={EMPTY_SELECTED_IDS}
      onChange={handleChange}
      selectionMode="single"
      menuWidth={200}
      showItemDividers={false}
      menuPositionHorizontal="left"
      shouldStopEventPropagation
      renderTrigger={({onClick}) => (
        <StyledIconButton
          onClick={(e) => {
            e.stopPropagation();
            onClick(e);
          }}
        >
          <StyledIcon
            name="Actions-More-Vertical"
            appearance="neutral"
            size="large"
            color={designSystemToken('semantic.fg.tertiary')}
          />
        </StyledIconButton>
      )}
    />
  );
}

export default RecruitmentActionsMenu;
